import SoundAvatar from '../../Common/SoundAvatar.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import DuelReveal from './DuelReveal.jsx';
import WinningSoundsReveal from '../../Common/WinningSoundsReveal.jsx';
import '../../../styles/theme.css';

export function SoundOffHost({ room = {}, audioEngine, onForceAdvance }) {
  const lookupClip = useClipLookup();
  const currentPhase = room?.currentPhase || room?.phase || 'authoring';
  const modeState = room?.modeState || {};
  const duels = modeState?.duels || [];
  const currentDuelIndex = modeState?.currentDuelIndex || 0;
  const currentDuel = duels[currentDuelIndex];
  const players = room?.players || {};
  const playerList = Object.values(players).filter(p => !p.isHost);

  const authorSubmissions = modeState?.authorSubmissions || {};
  const submissions = modeState?.submissions || {};

  return (
    <div
      className="soundoff-host-container"
      data-testid="soundoff-host"
      style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', boxSizing: 'border-box' }}
    >
      {/* Header Banner matching HostReveal.dc.html */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--sd-ground, #08090B)',
          padding: '16px 24px',
          borderRadius: '16px',
          border: '1px solid var(--sd-line, #1A1F25)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace", fontSize: '12px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--sd-dim, #6E7982)' }}>
            Round {room?.currentRound || 1} · Sound Off
          </span>
          <div style={{ width: '1px', height: '15px', background: 'var(--sd-line-2, #1C2229)' }} />
          <span style={{ fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace", fontSize: '12px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--sd-dim, #6E7982)' }}>
            {duels.length > 0 ? `Duel ${currentDuelIndex + 1} of ${duels.length}` : 'Sound Off Mode'}
          </span>
        </div>
        {onForceAdvance && (
          <button
            type="button"
            data-testid="host-force-advance-btn"
            onClick={onForceAdvance}
            style={{
              minHeight: '44px',
              minWidth: '44px',
              padding: '0 16px',
              borderRadius: '10px',
              background: 'var(--sd-panel-2, #0E1116)',
              border: '1px solid var(--sd-line, #1A1F25)',
              color: 'var(--sd-dim, #6E7982)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            ⏩ Force Advance
          </button>
        )}
      </div>

      {/* Phase 1: Authoring */}
      {currentPhase === 'authoring' && (
        <div
          data-testid="host-authoring-view"
          style={{
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            textAlign: 'center',
            padding: '48px 24px',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✍️</div>
          <h2 style={{
            fontSize: '2rem',
            color: 'var(--sd-ink, #EDEFF2)',
            margin: '0 0 12px 0',
            fontWeight: 800,
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}>
            PLAYERS ARE AUTHORING PROMPTS
          </h2>
          <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1.1rem', marginBottom: '28px' }}>
            Write a prompt on your phone! Prompts will be dealt out into head-to-head duels.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {playerList.map(player => {
              const hasSubmitted = Boolean(authorSubmissions[player.id]);
              return (
                <div
                  key={player.id}
                  style={{
                    padding: '8px 18px',
                    minHeight: '44px',
                    borderRadius: '12px',
                    background: hasSubmitted ? 'rgba(143, 199, 160, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${hasSubmitted ? 'rgba(143, 199, 160, 0.35)' : 'var(--sd-line, #1A1F25)'}`,
                    color: hasSubmitted ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {player.name} {hasSubmitted ? '✓' : '…'}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase 2: Answering */}
      {currentPhase === 'answering' && (
        <div
          data-testid="host-answering-view"
          style={{
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            textAlign: 'center',
            padding: '48px 24px',
          }}
        >
          <h2 style={{
            fontSize: '2rem',
            color: 'var(--sd-ink, #EDEFF2)',
            margin: '0 0 12px 0',
            fontWeight: 800,
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}>
            PICK YOUR 2 SOUND CLIPS!
          </h2>
          <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1.1rem', marginBottom: '28px' }}>
            Look at your phone to view your assigned prompt and select two matching clips.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {playerList.map(player => {
              const hasSubmitted = Boolean(submissions[player.id]);
              return (
                <div
                  key={player.id}
                  style={{
                    padding: '8px 18px',
                    minHeight: '44px',
                    borderRadius: '12px',
                    background: hasSubmitted ? 'rgba(143, 199, 160, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${hasSubmitted ? 'rgba(143, 199, 160, 0.35)' : 'var(--sd-line, #1A1F25)'}`,
                    color: hasSubmitted ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {player.name} {hasSubmitted ? '✓' : '…'}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase 3: Duel Reveal */}
      {(currentPhase === 'duel' || currentPhase === 'reveal') && (
        <div data-testid="host-duel-view">
          {currentDuel ? (
            <DuelReveal
              duel={currentDuel}
              submissions={submissions}
              currentPlayerId="host"
              audioEngine={audioEngine}
              isHost={true}
            />
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--sd-panel, #0D1015)',
              borderRadius: '20px',
              border: '1px solid var(--sd-line, #1A1F25)',
            }}>
              <h2 style={{ color: 'var(--sd-ink, #EDEFF2)' }}>Preparing Sound Off Duels...</h2>
            </div>
          )}
        </div>
      )}

      {/* Phase 4: Results & Leaderboard */}
      {(currentPhase === 'results' || currentPhase === 'game_over') && (
        <div
          data-testid="host-results-view"
          style={{
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
            padding: '36px',
          }}
        >
          <h2 style={{
            fontSize: '2rem',
            color: 'var(--sd-teal, #3ACFCF)',
            textAlign: 'center',
            marginBottom: '28px',
            fontWeight: 800,
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}>
            🏆 SOUND OFF LEADERBOARD 🏆
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {playerList
              .sort((a, b) => (b.score || 0) - (a.score || 0))
              .map((player, idx) => (
                <div
                  key={player.id}
                  data-testid={`leaderboard-row-${player.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: idx === 0 ? 'rgba(224, 164, 88, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${idx === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line, #1A1F25)'}`,
                    borderRadius: '14px',
                    padding: '16px 24px',
                    minHeight: '56px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{
                      fontSize: '1.2rem',
                      fontWeight: 800,
                      color: idx === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    }}>
                      #{idx + 1}
                    </span>
                    <SoundAvatar clip={lookupClip(player.avatar)} seed={player.name} size="sm" />
                    <span style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--sd-ink, #EDEFF2)' }}>{player.name}</span>
                  </div>
                  <div style={{
                    fontWeight: 800,
                    fontSize: '1.3rem',
                    color: 'var(--sd-teal, #3ACFCF)',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  }}>
                    {player.score || 0} PTS
                  </div>
                </div>
              ))}
          </div>

          <WinningSoundsReveal room={room} audioEngine={audioEngine} />
        </div>
      )}
    </div>
  );
}

export default SoundOffHost;
